import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './Auth.css';

import { API } from '../config';

export default function Login({ setUser }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFAState, setTwoFAState] = useState(null); // { session_id, email_hint }
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginType = searchParams.get('type');

  const handleLoginSuccess = (data) => {
    localStorage.setItem('token', data.token);
    setUser(data.user);
    toast.success('Login successful!');
    if (data.user.role === 'leadership' || data.user.role === 'super_admin') {
      navigate('/dashboard');
    } else {
      navigate('/group-selection');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      if (response.data.requires_2fa) {
        setTwoFAState({
          session_id: response.data.session_id,
          email_hint: response.data.email_hint
        });
        toast.success('Verification code sent to your email');
      } else {
        handleLoginSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (twoFACode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifying2FA(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        session_id: twoFAState.session_id,
        code: twoFACode
      });
      handleLoginSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      if (response.data.requires_2fa) {
        setTwoFAState({
          session_id: response.data.session_id,
          email_hint: response.data.email_hint
        });
        setTwoFACode('');
        toast.success('New verification code sent');
      }
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Verification Screen
  if (twoFAState) {
    return (
      <div className="auth-page" data-testid="two-fa-page">
        <div className="auth-container">
          <div className="auth-card card">
            <div className="auth-header">
              <h1>Two-Factor Verification</h1>
              <p>A 6-digit code has been sent to <strong>{twoFAState.email_hint}</strong></p>
            </div>

            <form onSubmit={handleVerify2FA} data-testid="two-fa-form">
              <div className="form-group">
                <label>Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  autoFocus
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '24px', 
                    letterSpacing: '8px', 
                    fontWeight: '700' 
                  }}
                  data-testid="two-fa-code-input"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary full-width"
                disabled={verifying2FA || twoFACode.length !== 6}
                data-testid="two-fa-submit-btn"
              >
                {verifying2FA ? 'Verifying...' : 'Verify & Login'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                onClick={handleResendCode}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3B82F6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline'
                }}
                data-testid="resend-code-btn"
              >
                {loading ? 'Sending...' : 'Resend Code'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                onClick={() => { setTwoFAState(null); setTwoFACode(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6B7280',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                data-testid="back-to-login-btn"
              >
                Back to Login
              </button>
            </div>

            <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '12px', marginTop: '16px' }}>
              Code expires in 5 minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-container">
        <div className="auth-card card">
          <div className="auth-header">
            <h1>
              {loginType === 'leadership' ? 'Leadership' : 
               loginType === 'member' ? 'Member' : 
               loginType === 'admin' ? 'Super Admin' : ''} Login
            </h1>
            <p>Access your {loginType === 'admin' ? 'admin panel' : 
               loginType === 'member' ? 'member portal' : 'group management dashboard'}</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
                <button 
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary full-width" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="forgot-password-link">
            <Link to="/forgot-password">Forgot your password?</Link>
          </div>

          <div className="auth-footer">
            Don't have an account? <Link to="/register">Sign up here</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
